import React, { useState, useEffect } from 'react';
import { marked } from 'marked';

interface MarkdownRendererProps {
  filePath: string;
  className?: string;
  onNavigate?: (path: string) => void;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  filePath, 
  className,
  onNavigate 
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`/docs/${filePath}`);
        if (!response.ok) {
          throw new Error(`Failed to load ${filePath}`);
        }
        
        const markdown = await response.text();
        marked.setOptions({ gfm: true, breaks: true });
        const html = await marked(markdown);
        setContent(html);
      } catch (err) {
        console.error('Error loading markdown:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    if (filePath) {
      loadMarkdown();
    }
  }, [filePath]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLAnchorElement;
    if (target && target.tagName === 'A') {
      const href = target.getAttribute('href');
      if (href) {
        if (href.startsWith('http') || href.startsWith('https') || href.startsWith('mailto:')) {
          event.preventDefault();
          try { window.open(href, '_blank', 'noopener,noreferrer'); return; }
          catch (e) { console.warn('Failed to open external link:', e); }
        }
        else if (href.endsWith('.md') || (!href.startsWith('http') && !href.startsWith('mailto:'))) {
          event.preventDefault();
          if (onNavigate) onNavigate(href.replace('.md', ''));
        }
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-slate-400">문서를 불러오는 중...</div></div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-64"><div className="text-red-400">문서를 불러올 수 없습니다: {error}</div></div>;
  }

  return (
    <div 
      onClick={handleClick} 
      className={`markdown-body ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: content }}
      style={{ boxSizing: 'border-box', minWidth: '200px', maxWidth: '100%', margin: '0' }}
    />
  );
};
